import { action, observable, runInAction } from 'mobx';
import { ITaxRate } from 'src/Interfaces/AdminInterface';
import { API } from 'src/Services/Api/Api';
import DeepCopy from 'src/UtilityFunctions/DeepCopy';
import { ICheckDate, IContact, IContractor, IEngineer, IOperator, IRegion, IWarehouse, IWell, IWellCloseReview, IWellCloseReviewExpense, IWellCloseReviewProduct, IWellIntervalCostExpense, IWellIntervalCostProduct } from '../../Interfaces/WellInterface';
import ConvertNulls from '../../Services/ConvertNulls';
import { CalcStore } from '../Calc/CalcStore';
import { MessageStore } from '../Messages/Messages';
import { RootStore } from '../Root/RootStore';

class WellsApi {
    public api: API;

    constructor(Api: API) {
        this.api = Api;
    }

    public getWells() {
        return this.api.get('/wells')
            .then(
                (data: any) => {
                    return data;
                },
                (error: any) => {
                    throw error;
                }
            );
    }

    public getWellsCopyList() {
        return this.api.get('/wells/copylist');
    }

    public getWellReportList() {
        return this.api.get('/wells/reportlist');
    }

    public getAllWellsNoFilter() {
        return this.api.get('/wells/all');
    }

    public getAllFinishingWellsNoFilter() {
        return this.api.get('/wells/allfinishing');
    }

    public getWell(wellId: string) {
        return this.api.get('/wells/' + wellId)
            .then(
                (data: any) => data,
                (error: any) => { throw error; }
            );
    }


    public getWellCopy(wellId: string) {
        return this.api.get('/wells/' + wellId + '/copydata');
    }

    public getWellReport(wellId: string) {
        return this.api.get('/wells/' + wellId + '/report')
            .then(
                (data: any) => data,
                (error: any) => { throw error; }
            );
    }

    public getFinishingWells() {
        return this.api.get('/wells/finishing');
    }

    public getClosedWells() {
        return this.api.get('/wells/closed');
    }

    public archiveWell(wellId: string) {
        return this.api.post(`/wells/${wellId}/archive`, {});
    }

    public deleteWell(wellId: string) {
        return this.api.delete(`/wells/${wellId}`);
    }

    public startClosingWell(wellId: string) {
        return this.api.post(`/wells/${wellId}/startclose`, {});
    }

    public closeWell(wellId: string) {
        return this.api.post(`/wells/${wellId}/close`, {});
    }

    public getWellCloseReview(wellId: string): Promise<IWellCloseReview> {
        return this.api.get(`/wells/${wellId}/wellclosereview`);
    }

    public async deleteEBPItem(wcreId: string) {
        return this.api.delete(`/wells/${wcreId}/wellclosereview`);
    }

    public async deleteProductItem(productId: string) {
        return this.api.delete(`/wells/wellclosereview/productsreview/${productId}`);
    }

    public updateWellCloseReview(wellId: string, wellCloseReview: IWellCloseReview) {
        return this.api.put(`/wells/${wellId}/wellclosereview`, wellCloseReview);
    }
    public postWell(well: IWell) {
        return this.api.post('/wells', well)
            .then(
                (data: any) => data,
                (error: any) => { throw error; }
            );
    }

    public getOperators() {
        return this.api.get('/operators')
            .then(
                (data: IOperator[]) => {
                    return data;
                },
                (error: any) => { throw error; }
            );
    }

    public getRegions() {
        return this.api.get('/regions');
    }

    // public checkStartDate(checkDate: ICheckDate) {
    //     return this.api.post('/wells/dates', checkDate);
    // }

    public getContractors() {
        return this.api.get('/contractors')
            .then(
                (data: IContractor[]) => data,
                (error: any) => { throw error; }
            );
    } 

    public getOperatorContacts(operatorId: any) {
        return this.api.get('/operators/' + operatorId + '/contacts')
            .then(
                (data: IOperator[]) => {
                    return data;
                },
                (error: any) => { throw error; }
            );
    }

    public getContractorContacts(contractorId: any) {
        return this.api.get('/contractors/' + contractorId + '/contacts')
            .then(
                (data: IContractor[]) => data,
                (error: any) => { throw error; }
            );
    }

    public getOtherContacts() {
        return this.api.get('/othercontacts')
            .then(
                (data: IContact[]) => data,
                (error: any) => { throw error; }
            );
    }

    public getTenantTaxRates(): Promise<ITaxRate[]> {
        return this.api.get('/products/taxrate');
    }

    public getEngineers() {
        return this.api.get('/account/engineers')
            .then(
                (data: IEngineer[]) => data,
                (error: any) => { throw error; }
            );
    }

    public getWarehouses() {
        return this.api.get('/warehouses')
            .then(
                (data: IWarehouse[]) => data,
                (error: any) => { throw error; }
            );
    }

    public getCounties(state?: string) {
        const append: string = (state) ? '/' + state : '';

        return this.api.get('/static/counties' + append)
            .then(
                (data: any) => data,
                (error: any) => { throw error; }
            );
    }

    public getUSStates() {
        return this.api.get('/static/usstates')
            .then(
                (data: any) => data,
                (error: any) => { throw error; }
            );
    }


    public getInternalEmailGroupOptions() {
        return this.api.get('/optionalfieldlists/InternalEmailGroupOptions')
            .then(
                (data: any) => {
                    return data.optionalFieldListItems;
                },
                (error: any) => {
                    throw error;
                }
            );
    }

    public getTargetFormationOptions() {
        return this.api.get('/optionalfieldlists/TargetFormationOptions')
            .then(data => data.optionalFieldListItems)
            .catch(error => {
                throw error;
            });
    }

    public getWellRecap(wellId: string, type: string) {
        return this.api.get('/reports/recap/' + wellId + '/' + type)
            .then(
                (data: any) => {
                    return data;
                },
                (error: any) => {
                    throw error;
                }
            );
    }

    public postWellFile(wellId: string, file: FormData) {
        return this.api.file(`/wells/${wellId}/file`, file);
    }

    public putWell(well: IWell) {
        return this.api.put('/wells/' + well.id, well)
            .then(
                (data: any) => data,
                (error: any) => { throw error; }
            );
    }

    public getContacts = (filter?: string) => {
        return this.api.get('/othercontacts')
            .then(
                (data: IContact[]) => data,
                (error: any) => { throw error; }
            );
    }


    public getWellFile(fileId: string) {
        return this.api.getFileFetchWithHeaders(`/wells/file/${fileId}`);
    }

    public deleteWellFile(fileId: string) {
        return this.api.delete(`/wells/file/${fileId}`);
    }


}

export class WellsStore {
    public convertNulls = new ConvertNulls();
    public api: WellsApi;
    private unassignedWell: IWell;
    @observable public wells: IWell[];
    @observable public well: IWell;
    @observable public copyWell: IWell;
    @observable public copyWellList: IWell[];
    @observable public operators: IOperator[];
    @observable public regions: IRegion[];
    @observable public contractors: IContractor[];
    @observable public otherContacts: IContact[];
    @observable public engineers: IEngineer[];
    @observable public warehouses: IWarehouse[];
    @observable public counties: any[];
    @observable public effectiveDates: any[];
    @observable public usStates: any[];
    @observable public contacts: IContact[];
    @observable public InternalEmailGroups: any[] = [];
    @observable public targetFormationList: any[] = [];
    @observable public wellCloseReview: IWellCloseReview;

    private messages: MessageStore;
    private RootStore: RootStore;
    private calcs: CalcStore;
    constructor(rootStore: RootStore) {
        this.RootStore = rootStore;
        this.messages = this.RootStore.MessageStore;
        this.calcs = this.RootStore.CalcStore;
        this.api = new WellsApi(this.RootStore.Api);
    }


    public getWells = async () => {
        const list: IWell[] = await this.api.getWells();

        runInAction(() => {
            this.wells = list;
        });
        return list;
    }
    @action
    public getWellReportList = async () => {
        const list: IWell[] = await this.api.getWellReportList();
        this.wells = list;
        return list;
    }
    @action
    public getWellsNoFilter = async () => {
        const list: IWell[] = await this.api.getAllWellsNoFilter();
        if (list) {
            this.wells = list;
        }
        return list;
    }

    @action
    public getAllFinishingWells = async () => {
        const list: IWell[] = await this.api.getAllFinishingWellsNoFilter();
        if (list) {
            this.wells = list;
        }
        return list;
    }

    @action
    public getFinishingWells = async () => {
        const list: IWell[] = await this.api.getFinishingWells();
        if (list) {
            this.wells = list;
        }
        return list;
    }

    @action
    public getClosedWells = async () => {
        const list: IWell[] = await this.api.getClosedWells();
        if (list) {
            this.wells = list;
        }
        return list;
    }

    public canUpdateWell = () => {
        if (this.well && this.well.id !== '') {
            if (this.RootStore.UserStore.hasPermission('Permissions.Wells.Update.All')
                || (
                    this.RootStore.UserStore.hasPermission('Permissions.Wells.Update.Assigned')
                    && this.well.engineers.some(eng => eng.id === this.RootStore.UserStore.user.id)
                )
            ) {
                return true;
            }
        }
        return false;
    }

    public closeWell() {
        return this.api.closeWell(this.well.id);
    }

    public startWellClose() {
        return this.api.startClosingWell(this.well.id);
    }

    public async getWellFile(fileId: string, filename: string) {
        return this.api.getWellFile(fileId)
            .then(response => {
                if (response.ok) {
                    return response;
                } else {
                    throw new Error('Failed to download file');
                }
            })
            .then(res => res.blob())
            .then(blob => {
                const file = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = file;
                link.setAttribute('download', filename);  // 3. Append to html page
                document.body.appendChild(link);  // 4. Force download
                link.click();  // 5. Clean up and remove the link
                if (link.parentNode) {
                    link.parentNode.removeChild(link);
                }
            })
            .catch(error => {
                this.RootStore.MessageStore.addMessage(error, 'danger');
            });

    }

    public async openWellFileInNewTab(fileId: string, filename: string) {
        return this.api.getWellFile(fileId)
            .then(response => {
                if (response.ok) {
                    return response;
                } else {
                    throw new Error('Failed to download file');
                }
            })
            .then(res => res.blob())
            .then(blob => {
                const file = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = file;
                link.setAttribute('target', '_blank');  // 3. Append to html page
                document.body.appendChild(link);  // 4. Force download
                link.click();  // 5. Clean up and remove the link
                if (link.parentNode) {
                    link.parentNode.removeChild(link);
                }
            })
            .catch(error => {
                this.RootStore.MessageStore.addMessage(error, 'danger');
            });

    }

    public postWellFile(wellId: string, file: any) {
        return this.api.postWellFile(wellId, file);
    }

    // public checkForStartDates = (well: IWell) => {
    //     let needMessage = false;

    //     if (well.operator.id == null && well.region.id == null)
    //     {
    //         return;
    //     }
    //     else {
    //         needMessage = true;
    //     }
       
       
    //     if (needMessage)
    //     {
    //         this.RootStore.MessageStore.addMessage('Current Well Has A Future Price List Beginning Within 7 Days Of Current Start Date', 'warning');
    //     }
    // }

    public deleteWellFile(fileId: string) {
        return this.api.deleteWellFile(fileId);
    }

    @action
    public deleteWell() {
        return this.api.deleteWell(this.well.id)
            .then(data => {
                if (data) {
                    return data;
                }
            });
    }

    @action
    public archiveWell() {
        return this.api.archiveWell(this.well.id)
            .then(data => {
                if (data) {
                    this.well.wellStatus = "Archived";
                    return data;
                }
            });
    }

    @action
    public async getWellCloseReview() {
        const getWellCloseReview = await this.api.getWellCloseReview(this.well.id);
        if (getWellCloseReview) {
            this.wellCloseReview = this.convertNulls.recurseObject(getWellCloseReview);
        }
        this.saveWellCloseReview(getWellCloseReview);
        return getWellCloseReview;
    }

    @action
    public deleteEBPItem = (wcreId: string) => {
        return this.api.deleteEBPItem(wcreId);
    }


    @action
    public deleteProductItem = (productId: string) => {
        return this.api.deleteProductItem(productId);
    }

    @action
    public clearWellCloseReview() {
        this.wellCloseReview = undefined as unknown as IWellCloseReview;
    }

    @action
    public updateWellCloseReview(wellCloseReview: IWellCloseReview) {
        this.updateWellCloseCalculations(wellCloseReview);

        this.wellCloseReview = DeepCopy(wellCloseReview);
    }

    // @action
    //     public checkStartDates(checkDates: ICheckDate)  {
    //     return this.api.checkStartDate(checkDates);
    // }

    // public checkStartDates = (checkDates: ICheckDate, operator: IOperator[]) => (index: any) => {
    //     const chosenOperator = operator[index].regions.find(ed => ed.id === checkDates.operatorId && ed.id === checkDates.regionId);
    //     console.log(chosenOperator);
        
    //     if (chosenOperator !== undefined) {
    //     const d1 = Date.parse(checkDates.startDate);
    //     const d2 = Date.parse(chosenOperator.effectiveDate);
      
    //     if (d1 < d2) {
    //         alert ("Error!");
    //     }
    // }
    // }

    public checkStartDates = (checkDates: ICheckDate, well: IWell) => {
        // const checkDates = this.state.checkDate;
        // const region = this.state.well.region;
        checkDates.startDate = well.startDate;
         let needMessage = false;
      
        const sd = new Date(checkDates.startDate);
        const ed = new Date(well.region.effectiveDate);
        console.log(sd, ed);
      
          if (ed > sd) {
            sd.setDate(sd.getDate() + 7);

            if (sd > ed)
            {
                needMessage = true;
            }
          }
          if (needMessage)
          {
            this.RootStore.MessageStore.addMessage(`The following future Price List is within 7 days of the Well Start Date that you chose: ${well.region.effectiveDate}. If you want the future Price List automatically assigned to this Well then update the Start Date field with a date on or after the future Price List’s effective date.`, 'warning');
          }
    }

    @action
    public updateWellCloseVolumes(wellCloseReview: IWellCloseReview) {
        const va = wellCloseReview.volumeAccounting;
        va.baseOilUsed = Math.round(this.calcs.calculateBaseOilUsed({
            baseOilReceived: Number(va.baseOilReceived),
            endingBaseOil: Number(va.endingBaseOil),
            startingBaseOil: Number(va.startingBaseOil),
        }));
        va.cumBaseOilUsed = Math.round(this.calcs.calculateCumBaseOilUsed(Number(va.baseOilUsed), Number(va.prevCumBaseOilUsed)));
        va.baseOil = Math.round(this.calcs.calculateBaseOilUsedBarrels(Number(va.baseOilUsed)));

        va.totalAdditions = Math.round(this.calcs.calculateTotalAdditions({
            baseOilUsedBarrels: Number(va.baseOil),
            chemicalAdditions: Number(va.chemicalAdditions),
            otherAdditions: Number(va.otherAdditions),
            otherReceived: 0,
            plantVolRecvd: 0,  // mud-1576
            slurry: Number(va.slurry),
            washout: Number(va.waterFlow),
            water: Number(va.water),
            xferFromStorage: 0
        }));

        va.totalVolAdditions = va.totalAdditions;

        va.totalLosses = Math.round(this.calcs.calculateTotalLosses({
            lossCuttings: 0,
            lossFormation: Number(va.lossToFormation),
            lossSurface: Number(va.lossToSurface),
            mudInHole: Number(va.mudLeftInHole),
            otherLosses: Number(va.otherLoss),
            otherTransfered: 0,
            plantVolReturned: 0,
            solidsControl: Number(va.solidsControl),
        }));

        va.totalVolLosses = va.totalLosses;

        va.reconciliationFactor =
            Number(va.finalTestVolume)
            + Number(va.totalVolAdditions)
            - Number(va.totalVolLosses)
            - Number(va.otherVolTransferred)
            - Number(va.plantVolReturned);

        va.volOwedToPlant = Number(va.prevVolOwedToPlant) - Number(va.plantVolReturned);
    }

    public updateWellCloseCalculations(wellCloseReview: IWellCloseReview) {
        let totalCost = 0;
        let totalTax = 0;
        wellCloseReview.products.forEach((wcp) => {
            const finalCost = (wcp.totalUsed + wcp.difference) * wcp.price;
            const tax = wcp.isTaxable ? finalCost * wellCloseReview.productTaxRate / 100 : 0;
            totalCost += finalCost;
            totalTax += tax;
        });
        wellCloseReview.adjustedProductTotal = totalCost;
        wellCloseReview.adjustedProductTax = totalTax;

        totalCost = 0;
        totalTax = 0;

        wellCloseReview.expenses.forEach((wcre) => {
            const finalCost = wcre.finalUnits * wcre.unitPrice;
            if (wcre.isTaxable) {
                if (wcre.expenseType.isRental) {
                    totalTax += finalCost * wellCloseReview.rentalTaxRate / 100;
                } else {
                    totalTax += finalCost * wellCloseReview.serviceTaxRate / 100;
                }
            }
            totalCost += finalCost;
        });

        wellCloseReview.adjustedOtherCosts = totalCost;
        wellCloseReview.adjustedOtherCostTax = totalTax;
        wellCloseReview.adjustedWellTotal =
            wellCloseReview.adjustedProductTotal
            + wellCloseReview.adjustedProductTax
            + wellCloseReview.adjustedOtherCosts
            + wellCloseReview.adjustedOtherCostTax;
        wellCloseReview.totalDifferenceCost = wellCloseReview.adjustedWellTotal - wellCloseReview.wellTotal;
        wellCloseReview.totalDifferencePercent = Number(Number(wellCloseReview.totalDifferenceCost / wellCloseReview.wellTotal * 100).toFixed(2));
    }

    public updateIntervalCosts(wellCloseReview: IWellCloseReview) {
        const intervalProductTotal = DeepCopy(wellCloseReview.products) as IWellCloseReviewProduct[];
        const intervalExpenseTotal = DeepCopy(wellCloseReview.expenses) as IWellCloseReviewExpense[];
        wellCloseReview.intervalCosts.forEach(value => {
            value.products.forEach(prod => {
                const intervalProduct = intervalProductTotal.find(p => p.productId === prod.product.id && p.price === prod.price);
                if (intervalProduct) {
                    intervalProduct.totalUsed -= prod.quantity;
                }
            });

            value.expenses.forEach(exp => {
                const intervalExpense = intervalExpenseTotal.find(e => e.expenseTypeId === exp.expense.id && e.unitPrice === exp.price);
                if (intervalExpense) {
                    intervalExpense.finalUnits -= exp.quantity;
                }
            });
        });

        intervalProductTotal.forEach(ipt => {
            let total = ipt.totalUsed + ipt.difference;
            if (total > 0) {
                const lastIntervalProduct = wellCloseReview.intervalCosts[wellCloseReview.intervalCosts.length - 1].products
                    .find(icp => icp.product.id === ipt.productId && icp.price === ipt.price);
                if (lastIntervalProduct) {
                    lastIntervalProduct.quantity += total;
                    lastIntervalProduct.quantity = Number(lastIntervalProduct.quantity.toFixed(2));
                } else {
                    const newLastIntervalProduct: IWellIntervalCostProduct = {
                        id: '',
                        price: ipt.price,
                        product: ipt.product,
                        quantity: total,
                    };
                    wellCloseReview.intervalCosts[wellCloseReview.intervalCosts.length - 1].products.push(newLastIntervalProduct);
                }
            } else if (total < 0) {
                const index = wellCloseReview.intervalCosts.length - 1;
                for (let i = index; i >= 0; i--) {
                    const lastInterval = wellCloseReview.intervalCosts[i];
                    const lastIntervalProduct = lastInterval.products.find(lip => lip.product.id === ipt.productId && lip.price === ipt.price);
                    if (lastIntervalProduct) {
                        const diffQuant = lastIntervalProduct.quantity + total;
                        if (diffQuant >= 0) {
                            lastIntervalProduct.quantity = Number(diffQuant.toFixed(2));
                            break;
                        } else {
                            lastIntervalProduct.quantity = 0;
                            total = diffQuant;
                        }
                    }
                }
            }
        });

        intervalExpenseTotal.forEach(iet => {
            let total = iet.finalUnits;
            if (total > 0) {
                const lastIntervalExpense = wellCloseReview.intervalCosts[wellCloseReview.intervalCosts.length - 1].expenses
                    .find(iExp => iExp.expense.id === iet.expenseTypeId && iExp.price === iet.unitPrice);
                if (lastIntervalExpense) {
                    lastIntervalExpense.quantity += total;
                } else {
                    const newLastIntervalExpense: IWellIntervalCostExpense = {
                        expense: iet.expenseType,
                        id: '',
                        price: iet.unitPrice,
                        quantity: total,
                    };
                    wellCloseReview.intervalCosts[wellCloseReview.intervalCosts.length - 1].expenses.push(newLastIntervalExpense);
                }
            }
            else if (total < 0) {
                const index = wellCloseReview.intervalCosts.length - 1;
                for (let i = index; i >= 0; i--) {
                    const lastInterval = wellCloseReview.intervalCosts[i];
                    const lastIntervalExpense = lastInterval.expenses.find(iExp => iExp.expense.id === iet.expenseTypeId && iExp.price === iet.unitPrice);
                    if (lastIntervalExpense) {
                        const diffQuant = lastIntervalExpense.quantity + total;
                        if (diffQuant >= 0) {
                            lastIntervalExpense.quantity = diffQuant;
                            break;
                        } else {
                            lastIntervalExpense.quantity = 0;
                            total = diffQuant;
                        }
                    }
                }
            }
        });
    }

    @action
    public async saveWellCloseReview(wellCloseReview: IWellCloseReview) {
        this.updateIntervalCosts(wellCloseReview);
        this.updateWellCloseVolumes(wellCloseReview);
        const result = await this.api.updateWellCloseReview(wellCloseReview.wellId, wellCloseReview);
        if (result) {
            this.RootStore.MessageStore.addMessage('Well Close Review saved.', 'success');
        }
        this.wellCloseReview = wellCloseReview;
        return result;
    }

    public createNewWellInstance() {
        runInAction(() => {
            this.well = new IWell();
        });
        return this.well;
    }

    @action
    public clearWell = () => {
        this.well = this.unassignedWell;
    }

    public getWellDetails = async (wellId: string) => {
        const gotWell = await this.api.getWell(wellId);
        if (gotWell) {

            runInAction(() => {
                const noNulls = this.convertNulls.recurseObject(gotWell);
                this.well = noNulls;
            });
        }
        return gotWell;
    }

    public getCopyWellDetails = async (wellId: string) => {
        const gotWell = await this.api.getWellCopy(wellId);
        if (gotWell) {

            runInAction(() => {
                const noNulls = this.convertNulls.recurseObject(gotWell);
                this.copyWell = noNulls;
            });
        }
        return gotWell;
    }

    @action
    public clearCopyWellDetails = () => {
        this.copyWell = undefined as unknown as IWell;
    }

    public getCopyWellList = async () => {
        const wells = await this.api.getWellsCopyList();
        if (wells) {
            runInAction(() => {
                this.copyWellList = wells;
            });
        }
    }


    public getWellReport = async (wellId: string) => {
        const gotWell = await this.api.getWellReport(wellId);
        if (gotWell) {

            runInAction(() => {
                const noNulls = this.convertNulls.recurseObject(gotWell);
                this.well = noNulls;
            });
        }
        return gotWell;
    }

    public updateWellModel = async (well: IWell) => {
        if (well.id === "") {
            return this.postWellModel(well);
        } else {
            return this.putWellModel(well);
        }
    }

    @action
    public postWellModel(well: IWell) {
        return this.api.postWell(well).then(data => {
            if (data) {
                this.messages.addMessage(
                    'Well successfully saved.',
                    'success'
                );
                const noNulls = this.convertNulls.recurseObject(data);
                this.well = noNulls;
            }
            return data;
        }
        );
    }

    @action
    public putWellModel(well: IWell) {
        return this.api.putWell(well).then(data => {
            if (data) {
                this.messages.addMessage(
                    'Well successfully saved.',
                    'success'
                );
                const noNulls = this.convertNulls.recurseObject(data);
                this.well = noNulls;
            }
            return data;
        });
    }

    public getInitialData() {

        this.api.getContractors().then((data: IContractor[]) => {
            if (data && Array.isArray(data)) {
                data.forEach(contractor => {
                    this.getContractorContacts(contractor.id);
                });
            }
        });
        this.api.getOperators().then((data: IOperator[]) => {
            if (data && Array.isArray(data)) {
                data.forEach(operator => {
                    this.getOperatorContacts(operator.id);
                });
            }
        });
        this.api.getUSStates().then(data => {
            if (data && Array.isArray(data)) {
                data.forEach(state => {
                    this.getCounties(state);
                });
            }
        });
        this.getEngineers();
        this.getWarehouses();
        this.getOtherContacts();
        this.getInternalGroupEmailList();
    }

    public getOperators = async () => {
        const list: IOperator[] = await this.api.getOperators();

        runInAction(() => {
            this.operators = list;
        });
    }

    public getRegions = async () => {
        const list: IRegion[] = await this.api.getRegions();

        runInAction(() => {
            this.regions = list;
        });
        return list;
    }

    public getContractors = async () => {
        const list: IContractor[] = await this.api.getContractors();

        runInAction(() => {
            this.contractors = list;
        });
    }

    public getInternalGroupEmailList = async () => {
        const list = await this.api.getInternalEmailGroupOptions();

        runInAction(() => {
            this.InternalEmailGroups = list;
        });
    }

    public getTargetFormationOptions = async () => {
        const list = await this.api.getTargetFormationOptions();

        runInAction(() => {
            this.targetFormationList = list;
        });
    }
    public getContactsType = async (id: any, type: any) => {
        switch (type) {
            case 'operator':
                {
                    this.getOperatorContacts(id);
                    break;
                }
            case 'contractor':
                {
                    this.getContractorContacts(id);
                    break;
                }
            case 'other':
                {
                    this.getOtherContacts();
                    break;
                }
        }
    }
    public getOperatorContacts = async (operatorId: any) => {
        const list: IOperator[] = await this.api.getOperatorContacts(operatorId);

        runInAction(() => {
            this.operators = list;
        });
    }

    public getContractorContacts = async (contractorId: any) => {
        const list: IContractor[] = await this.api.getContractorContacts(contractorId);

        runInAction(() => {
            this.contractors = list;
        });
    }

    public getEngineers = async () => {
        const list: IEngineer[] = await this.api.getEngineers();

        runInAction(() => {
            this.engineers = list;
        });
    }


    // public getEffectiveDates = async () => {
    //     const list: IEffectiveDates[] = await this.api.getEffectiveDates();

    //     runInAction(() => {
    //         this.effectiveDates = list;
    //     });
    // }

    public getOtherContacts = async () => {
        const list: IContact[] = await this.api.getOtherContacts();

        runInAction(() => {
            this.otherContacts = list;
        });
    }

    public getWarehouses = async () => {
        const list: IWarehouse[] = await this.api.getWarehouses();

        runInAction(() => {
            this.warehouses = list;
        });

        return list;
    }

    public getCounties = async (state?: string) => {
        const list: any[] = await this.api.getCounties(state);

        runInAction(() => {
            this.counties = list;
        });
    }

    public filterContacts = (filter: string) => {
        return this.api.getContacts(filter);
    }

    public getContacts = async () => {
        const list: any[] = await this.api.getContacts();

        runInAction(() => {
            this.contacts = list;
        });
    }

    public getUSStates = async () => {
        const list: IWarehouse[] = await this.api.getUSStates();

        runInAction(() => {
            const listArray: any[] = [];

            for (const prop in list) {
                if (list[prop]) {
                    listArray.push([list[prop], prop]);
                }
            }

            this.usStates = listArray;
        });
    }

    public getWellRecap(wellId: string, type: string) {
        return this.api.getWellRecap(wellId, type);
    }

    public getTaxRates() {
        return this.api.getTenantTaxRates();
    }
}


// WEBPACK FOOTER //
// ./src/Stores/Wells/WellsStore.tsx